import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  Stack,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  AttachMoney,
  Description,
  CloudUpload,
  Delete,
  Image,
  Description as DocumentIcon,
  CheckCircle,
  Visibility,
  Download,
} from "@mui/icons-material";

/**
 * PaymentForm - Form for submitting payment proof
 * Mobile-first responsive design for payment submission
 */
export const PaymentForm = ({
  paymentData,
  onPaymentDataChange,
  quoteAmount,
}) => {
  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    const formatted = new Intl.NumberFormat("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `ZAR ${formatted}`;
  };

  const BUSINESS_NAME = import.meta.env.VITE_BUSINESS_NAME || "";
  const BANK_NAME = import.meta.env.VITE_BANK_NAME || "FNB";
  const BANK_BRANCH_CODE = import.meta.env.VITE_BANK_BRANCH_CODE || "";
  const BANK_ACCOUNT_NUMBER = import.meta.env.VITE_BANK_ACCOUNT_NUMBER || "";
  const BANK_ACCOUNT_HOLDER =
    import.meta.env.VITE_BANK_ACCOUNT_HOLDER || BUSINESS_NAME || "";

  const handleInputChange = (field, value) => {
    onPaymentDataChange({
      ...paymentData,
      [field]: value,
    });
  };

  const paymentMethods = [
    { value: "eft", label: "EFT (Electronic Funds Transfer)" },
    { value: "cash_deposit", label: "Cash Bank Deposit" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "card", label: "Credit/Debit Card" },
    { value: "other", label: "Other" },
  ];

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const ALLOWED_MIME = ["image/jpeg", "image/png", "application/pdf"];
  const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Validate files before processing
    const rejected = [];
    const accepted = [];
    for (const f of files) {
      const isTypeOk = ALLOWED_MIME.includes(f.type);
      const isSizeOk = f.size <= MAX_SIZE_BYTES;
      if (isTypeOk && isSizeOk) {
        accepted.push(f);
      } else {
        rejected.push({
          name: f.name,
          reason: !isTypeOk ? "Unsupported type" : "File too large (>10MB)",
        });
      }
    }

    if (rejected.length > 0) {
      setUploadError(
        `Some files were rejected: ` +
          rejected.map((r) => `${r.name} (${r.reason})`).join(", ")
      );
    } else {
      setUploadError("");
    }

    if (accepted.length === 0) return;

    setUploading(true);
    try {
      // Simulate file upload - replace with actual upload logic
      const newFiles = accepted.map((file, index) => ({
        id: `file_${Date.now()}_${index}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        uploaded: false,
        progress: 0,
      }));

      // Simulate upload progress
      for (const file of newFiles) {
        for (let progress = 0; progress <= 100; progress += 10) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === file.id ? { ...f, progress } : f))
          );
        }
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === file.id ? { ...f, uploaded: true, progress: 100 } : f
          )
        );
      }

      setUploadedFiles((prev) => [...prev, ...newFiles]);

      // Update payment data with file references
      const allFiles = [...uploadedFiles, ...newFiles];
      handleInputChange("proofFiles", allFiles);
    } catch (error) {
      console.error("File upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter((f) => f.id !== fileId);
    setUploadedFiles(updatedFiles);
    handleInputChange("proofFiles", updatedFiles);
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) return <Image />;
    return <DocumentIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handlePreviewFile = (file) => {
    setPreviewFile(file);
  };

  const closePreview = () => {
    setPreviewFile(null);
  };

  const isImageFile = (fileType) => {
    return fileType.startsWith("image/");
  };

  const getFilePreviewUrl = (file) => {
    if (file.file) {
      return URL.createObjectURL(file.file);
    }
    return file.previewUrl || file.downloadUrl;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payment Details
      </Typography>

      {/* Amount */}
      <TextField
        fullWidth
        label="Amount Paid (ZAR)"
        type="number"
        value={paymentData.amount}
        onChange={(e) => handleInputChange("amount", e.target.value)}
        placeholder={quoteAmount ? formatCurrency(quoteAmount) : "0.00"}
        InputProps={{
          startAdornment: <AttachMoney color="action" />,
        }}
        sx={{ mb: 2 }}
        required
        helperText={
          quoteAmount ? `Quote amount: ${formatCurrency(quoteAmount)}` : ""
        }
      />

      {/* Payment Method */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Payment Method</InputLabel>
        <Select
          value={paymentData.paymentMethod}
          onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
          label="Payment Method"
        >
          {paymentMethods.map((method) => (
            <MenuItem key={method.value} value={method.value}>
              {method.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Payment Proof Upload */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Payment Proof Upload *
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload clear screenshots, receipts, or PDF documents (max 10MB per
          file).
        </Typography>

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </Alert>
        )}

        {/* File Upload Area */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: "center",
            border: "2px dashed",
            borderColor: "primary.main",
            bgcolor: "primary.50",
            cursor: "pointer",
            "&:hover": {
              bgcolor: "primary.100",
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUpload sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Upload Payment Proof
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click to select files or drag and drop
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Supported types: JPG, PNG, PDF · Max size: 10MB per file
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
        </Paper>

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Uploading files...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Uploaded Files ({uploadedFiles.length})
            </Typography>
            <Stack spacing={1}>
              {uploadedFiles.map((file) => (
                <Paper
                  key={file.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {isImageFile(file.type) && file.uploaded ? (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "grey.100",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={getFilePreviewUrl(file)}
                        alt={file.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ) : (
                    getFileIcon(file.type)
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                    {file.uploaded && (
                      <Chip
                        icon={<CheckCircle />}
                        label="Uploaded"
                        size="small"
                        color="success"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {!file.uploaded && (
                      <Box sx={{ width: "100%", mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={file.progress}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {file.uploaded && isImageFile(file.type) && (
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewFile(file)}
                        color="primary"
                        title="Preview"
                      >
                        <Visibility />
                      </IconButton>
                    )}
                    {file.uploaded && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          const url = getFilePreviewUrl(file);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download = file.name;
                          link.click();
                        }}
                        color="primary"
                        title="Download"
                      >
                        <Download />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeFile(file.id)}
                      color="error"
                      title="Remove"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Additional Notes */}
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Additional Notes (Optional)"
        value={paymentData.notes}
        onChange={(e) => handleInputChange("notes", e.target.value)}
        placeholder="Any additional information about your payment..."
        InputProps={{
          startAdornment: <Description color="action" />,
        }}
        sx={{ mb: 2 }}
      />

      {/* Payment Instructions */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Payment Instructions:</strong>
        </Typography>
        <Stack component="ul" sx={{ mt: 1, pl: 2, m: 0 }}>
          <Typography component="li" variant="body2">
            Use the quote ID as your payment reference
          </Typography>
          <Typography component="li" variant="body2">
            Upload clear screenshots or documents as proof
          </Typography>
          <Typography component="li" variant="body2">
            We&apos;ll verify your payment within 24 hours
          </Typography>
          <Typography component="li" variant="body2">
            You&apos;ll receive confirmation via WhatsApp/SMS
          </Typography>
        </Stack>
      </Alert>

      {/* Success Notice */}
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Important Notice:</strong> We will get back to you once the
          funds have cleared. Please allow 1-3 business days for bank
          processing.
        </Typography>
      </Alert>

      {/* Bank Details Reminder */}
      <Alert severity="success" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Bank Transfer Details:</strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Bank: {BANK_NAME}
          <br />
          Account: {BANK_ACCOUNT_HOLDER}
          <br />
          Account Number: {BANK_ACCOUNT_NUMBER}
          <br />
          Branch Code: {BANK_BRANCH_CODE}
          <br />
          Reference: Quote ID
        </Typography>
      </Alert>

      {/* File Preview Modal */}
      {previewFile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            p: 2,
          }}
          onClick={closePreview}
        >
          <Box
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              bgcolor: "white",
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">{previewFile.name}</Typography>
              <IconButton onClick={closePreview} color="error">
                <Delete />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 2, textAlign: "center" }}>
              {isImageFile(previewFile.type) ? (
                <img
                  src={getFilePreviewUrl(previewFile)}
                  alt={previewFile.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <DocumentIcon
                    sx={{ fontSize: 64, color: "text.secondary" }}
                  />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    {previewFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(previewFile.size)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Preview not available for this file type
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                p: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => {
                  const url = getFilePreviewUrl(previewFile);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = previewFile.name;
                  link.click();
                }}
              >
                Download
              </Button>
              <Button variant="contained" onClick={closePreview}>
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
